// The marketing header over the hero (#3200). The server renders the frosted
// bar with the site links and Book a demo, because they are the site's only
// navigation; this module takes the bar down to the original rarion.uk nav
// while the hero owns the screen, slides it away as the hero scrolls, and
// brings the frosted bar back once the hero has gone. Two
// IntersectionObservers and no scroll listener: one on a hairline sentinel at
// the hero's top edge (still intersecting for the first few pixels of
// scroll), one on the hero itself, measured below the header. Pages without
// a hero are left exactly as rendered.

export const STATE_ATTRIBUTE = "data-nav-state";
export const READY_ATTRIBUTE = "data-nav-ready";
export const SENTINEL_ATTRIBUTE = "data-nav-sentinel";
export const HEADER_ID = "marketing-header";
export const HERO_ID = "hero";

// How far the page may scroll before the bar leaves the over-hero state.
export const TOP_SLACK_PX = 32;

export type NavState = "over-hero" | "hidden" | "docked";

export interface NavHandle {
  dispose(): void;
}

export function stateFor(atTop: boolean, heroOnScreen: boolean): NavState {
  if (atTop) return "over-hero";
  return heroOnScreen ? "hidden" : "docked";
}

export function attachSentinel(hero: HTMLElement): HTMLElement {
  const sentinel = hero.ownerDocument.createElement("div");
  sentinel.setAttribute(SENTINEL_ATTRIBUTE, "");
  sentinel.setAttribute("aria-hidden", "true");
  Object.assign(sentinel.style, {
    position: "absolute",
    insetInline: "0",
    top: "0",
    height: "1px",
    pointerEvents: "none",
  });
  hero.appendChild(sentinel);
  return sentinel;
}

export function mount(header: HTMLElement, hero: HTMLElement): NavHandle {
  const sentinel = attachSentinel(hero);
  let atTop: boolean | null = null;
  let heroOnScreen: boolean | null = null;

  const apply = () => {
    if (atTop === null || heroOnScreen === null) return;
    header.setAttribute(STATE_ATTRIBUTE, stateFor(atTop, heroOnScreen));
    if (!header.hasAttribute(READY_ATTRIBUTE)) {
      // Settle the first state before the transitions apply to the header.
      void header.offsetWidth;
      header.setAttribute(READY_ATTRIBUTE, "");
    }
  };

  const topObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) atTop = entry.isIntersecting;
      apply();
    },
    { rootMargin: `${TOP_SLACK_PX}px 0px 0px 0px`, threshold: 0 },
  );
  const heroObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) heroOnScreen = entry.isIntersecting;
      apply();
    },
    { rootMargin: `-${header.offsetHeight}px 0px 0px 0px`, threshold: 0 },
  );
  topObserver.observe(sentinel);
  heroObserver.observe(hero);

  return {
    dispose() {
      topObserver.disconnect();
      heroObserver.disconnect();
      sentinel.remove();
      header.removeAttribute(STATE_ATTRIBUTE);
      header.removeAttribute(READY_ATTRIBUTE);
    },
  };
}

export function boot(root: Document = document): NavHandle | null {
  const header = root.getElementById(HEADER_ID);
  const hero = root.getElementById(HERO_ID);
  if (!header || !hero || !("IntersectionObserver" in window)) return null;
  return mount(header, hero);
}
